import { BarChartDatum } from "components/molecules/Charts/BarChart";
import { buildingTypeOptions, CarbonFinesForecastChartGroup } from "utils/constants";

export interface CarbonFinesForecastChartData {
    barChartData: BarChartDatum[];
    cumulativeCarbonFines: number;
}

export interface CarbonFinesForecastDatum {
    policyId: number;
    policyName: string;
    policyYear: number;
    city: string;
    stateProvince: string;
    buildingArchetype: string;
    propertyName: string;
    fundName: string;
    grossFloorArea: number;
    carbonFinesUnsaveable: number;
}

function carbonPolicyAggregator(carbonFinesForecastData: CarbonFinesForecastDatum[]) {
    const groupedData = Object.groupBy(carbonFinesForecastData, ({ policyName }) => policyName);
    const { yearStart, yearEnd } = getYearRange(carbonFinesForecastData);
    const aggregatedData = aggregateGroupedData(groupedData, yearStart, yearEnd);
    return summarizeAggregatedData(aggregatedData);
}

function buildingArchetypeAggregator(carbonFinesForecastData: CarbonFinesForecastDatum[]) {
    const groupedData = Object.groupBy(carbonFinesForecastData, ({ buildingArchetype }) => {
        const humanReadable = buildingTypeOptions.find(({ key }) => key === buildingArchetype)?.label ?? "undefined";
        return humanReadable;
    });
    const { yearStart, yearEnd } = getYearRange(carbonFinesForecastData);
    const aggregatedData = aggregateGroupedData(groupedData, yearStart, yearEnd);
    return summarizeAggregatedData(aggregatedData);
}

function regionAggregator(carbonFinesForecastData: CarbonFinesForecastDatum[]) {
    const groupedData = Object.groupBy(carbonFinesForecastData, ({ stateProvince }) => stateProvince);
    const { yearStart, yearEnd } = getYearRange(carbonFinesForecastData);
    const aggregatedData = aggregateGroupedData(groupedData, yearStart, yearEnd);
    return summarizeAggregatedData(aggregatedData);
}

function propertyAggregator(carbonFinesForecastData: CarbonFinesForecastDatum[]) {
    const groupedData = Object.groupBy(carbonFinesForecastData, ({ propertyName }) => propertyName ?? "no property");
    const { yearStart, yearEnd } = getYearRange(carbonFinesForecastData);
    const aggregatedData = aggregateGroupedData(groupedData, yearStart, yearEnd);
    return summarizeAggregatedData(aggregatedData);
}

function fundAggregator(carbonFinesForecastData: CarbonFinesForecastDatum[]) {
    const groupedData = Object.groupBy(carbonFinesForecastData, ({ fundName }) => fundName ?? "no fund");
    const { yearStart, yearEnd } = getYearRange(carbonFinesForecastData);
    const aggregatedData = aggregateGroupedData(groupedData, yearStart, yearEnd);
    return summarizeAggregatedData(aggregatedData);
}

export function getCarbonFinesForecastAggregator(carbonFinesForecastChartGroup: CarbonFinesForecastChartGroup) {
    const groupToAggregator = {
        carbonPolicy: carbonPolicyAggregator,
        property: propertyAggregator,
        fund: fundAggregator,
        region: regionAggregator,
        buildingArchetype: buildingArchetypeAggregator,
    };
    return groupToAggregator[carbonFinesForecastChartGroup];
}

function getYearRange(carbonFinesForecastData: CarbonFinesForecastDatum[]) {
    const carbonFinesForecastYears = carbonFinesForecastData.map((cffd) => cffd.policyYear);
    return {
        yearStart: Math.min(...carbonFinesForecastYears),
        yearEnd: Math.max(...carbonFinesForecastYears),
    };
}

function aggregateGroupedData(
    groupedData: Partial<Record<string, CarbonFinesForecastDatum[]>>,
    yearStart: number,
    yearEnd: number
): BarChartDatum[] {
    const yearRange = Array.from({ length: yearEnd + 1 - yearStart }, (_, i) => yearStart + i);

    interface BarChartDatumWithYear {
        year: number;
        [key: string]: number;
    }

    const groupNames = Object.keys(groupedData);
    const yearToBarChartDatum: Record<number, BarChartDatumWithYear> = Object.fromEntries(
        yearRange.map((year) => {
            const barChartDatumWithoutYear = Object.fromEntries(groupNames.map((groupName) => [groupName, 0]));
            return [year, { year, ...barChartDatumWithoutYear }];
        })
    );

    Object.entries(groupedData).forEach(([groupName, groupData]) => {
        if (groupData === undefined || groupData.length === 0) return;

        groupData.forEach((groupDatum) => {
            const { policyYear: year, carbonFinesUnsaveable: cost } = groupDatum;
            yearToBarChartDatum[year][groupName] += cost;
        });
    });

    return Object.values(yearToBarChartDatum);
}

function summarizeAggregatedData(barChartData: BarChartDatum[]): CarbonFinesForecastChartData {
    const cumulativeCarbonFines = barChartData.reduce(
        (acc, bcd) =>
            acc +
            Object.entries(bcd)
                .filter(([k, _]) => k !== titleKey)
                .reduce((acc, [_, v]) => (typeof v === "number" ? acc + v : acc), 0),
        0
    );
    return {
        barChartData: barChartData.sort((a, b) => Number(a.year) - Number(b.year)),
        cumulativeCarbonFines,
    };
}

export const titleKey = "year";
